import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientService from '../services/clientService';

const toOption = (c) => ({
  value: c.id,
  label: `${c.firstName || ''} ${c.lastName || ''}`.trim() + (c.nifCin ? ` — ${c.nifCin}` : ''),
  data: c,
});

const EMPTY_VALUES = {
  type: 'BUY',
  client: null,
  clientName: '',
  amountUSD: '',
  note: '',
};

export default function ExchangeModal({
  show,
  onHide,
  onSave,
  initialData = null,
  currentRates = null,
}) {
  const init = useMemo(() => initialData ?? null, [initialData]);
  const isEdit = !!init?.id;

  const [selectKey, setSelectKey] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_VALUES,
    shouldUnregister: true,
  });

  const selectedClient = watch('client');
  const type = String(watch('type') || 'BUY').toUpperCase();
  const amountUSD = Number(watch('amountUSD') || 0);

  const buyRate = Number(currentRates?.buyRateUsdHtg ?? currentRates?.usdToHtg ?? 0);
  const sellRate = Number(currentRates?.sellRateUsdHtg ?? currentRates?.usdToHtg ?? 0);
  const currentRate = type === 'BUY' ? buyRate : sellRate;
  const amountHTG = Number.isFinite(currentRate) && currentRate > 0
    ? Number((amountUSD * currentRate).toFixed(2))
    : 0;

  useEffect(() => {
    if (!show) return;

    reset({
      type: init?.type || 'BUY',
      client: init?.client ? toOption(init.client) : null,
      clientName: init?.clientName || '',
      amountUSD: init?.amountUSD || '',
      note: init?.note || '',
    });
    setSelectKey(k => k + 1);
  }, [show, init, reset]);

  const handleClose = () => {
    reset(EMPTY_VALUES);
    setSelectKey(k => k + 1);
    onHide();
  };

  useEffect(() => {
    if (!show) return;
    const c = selectedClient?.data;
    if (!c) return;

    const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    if (!fullName) return;

    const current = String(getValues('clientName') || '').trim();
    if (current !== fullName) {
      setValue('clientName', fullName, { shouldValidate: true });
    }
  }, [show, selectedClient?.value, selectedClient?.data, setValue, getValues]);

  const loadClientOptions = async (inputValue, loadedOptions, { page }) => {
    const res = await clientService.searchPaged({ q: inputValue || '', page, pageSize: 20 });
    const rows = res?.rows || [];
    const options = rows.map(toOption);

    return {
      options,
      hasMore: page < (res?.totalPages || 1),
      additional: { page: page + 1 },
    };
  };

  const submit = (vals) => {
    const clientId = vals.client?.value ?? null;

    const payload = {
      type: String(vals.type || '').toUpperCase(),
      clientId,
      clientName: clientId ? null : (String(vals.clientName || '').trim() || null),
      amountUSD: Number(vals.amountUSD),
      note: String(vals.note || '').trim() || null,
    };

    onSave(payload);
  };

  const clientNameDisabled = !!selectedClient?.value;
  const rateLabel = type === 'BUY' ? 'Taux achat' : 'Taux vente';

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Modifier Exchange' : 'Nouvelle transaction Exchange'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          <Alert variant="secondary" className="mb-3">
            <div className="fw-semibold">{rateLabel}</div>
            <div>
              {currentRate > 0
                ? `1 USD = ${currentRate.toFixed(2)} HTG`
                : 'Taux non défini dans Taux du jour'}
            </div>
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Type *</Form.Label>
                <Form.Select {...register('type', { required: 'Requis' })}>
                  <option value="BUY">Achat USD</option>
                  <option value="SELL">Vente USD</option>
                </Form.Select>
                {errors.type && <Form.Text className="text-danger">{errors.type.message}</Form.Text>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Montant (USD) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('amountUSD', {
                    required: 'Requis',
                    validate: (v) => (Number(v) > 0 ? true : 'Doit être > 0'),
                  })}
                />
                {errors.amountUSD && <Form.Text className="text-danger">{errors.amountUSD.message}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mt-3">
            <Form.Label>Client (optionnel)</Form.Label>
            <Controller
              control={control}
              name="client"
              render={({ field }) => (
                <AsyncPaginate
                  key={selectKey}
                  value={field.value}
                  onChange={field.onChange}
                  loadOptions={loadClientOptions}
                  additional={{ page: 1 }}
                  debounceTimeout={300}
                  placeholder="Sélectionner un client (facultatif)…"
                  isClearable
                />
              )}
            />
            <Form.Text className="text-muted">
              Si la personne n’est pas client, laisse vide et remplis le nom.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Nom du client / bénéficiaire {clientNameDisabled ? '(auto)' : '*'}</Form.Label>
            <Form.Control
              disabled={clientNameDisabled}
              {...register('clientName', {
                validate: (v) => {
                  if (selectedClient?.value) return true;
                  return String(v || '').trim() ? true : 'Requis si pas de client';
                },
              })}
            />
            {errors.clientName && <Form.Text className="text-danger">{errors.clientName.message}</Form.Text>}
          </Form.Group>

          <Alert variant="info" className="mt-3 mb-0">
            <div className="d-flex justify-content-between">
              <span>{rateLabel}</span>
              <strong>{currentRate > 0 ? currentRate.toFixed(2) : '—'}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Total HTG calculé</span>
              <strong>{amountHTG.toFixed(2)}</strong>
            </div>
            <div className="small text-muted mt-2">
              {type === 'BUY'
                ? 'Achat = le client apporte USD, vous décaissez HTG.'
                : 'Vente = vous remettez USD, vous encaissez HTG.'}
            </div>
          </Alert>

          <Form.Group className="mt-3">
            <Form.Label>Note</Form.Label>
            <Form.Control as="textarea" rows={3} {...register('note')} />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Annuler</Button>
          <Button type="submit" variant="primary" disabled={currentRate <= 0}>
            {isEdit ? 'Mettre à jour' : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}